import React, { useState, useEffect } from "react";
import {
  FaBullhorn,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaPlay,
} from "react-icons/fa";
import { FaStar } from "react-icons/fa";
import api from "../api/api";
import "./HomeHeaderSave.css";

const HomeHeader = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* =====================================================
     FETCH SLIDES
  ===================================================== */

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await api.get("/api/slides/");

        console.log("Slides API Response:", response.data);

        const data = response.data.results || response.data;

        setSlides(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load slides:", err);

        setError("Unable to load slides");
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  /* =====================================================
     AUTO SLIDER
  ===================================================== */

  useEffect(() => {
    if (slides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides]);

  /* =====================================================
     TEXT ANIMATION
  ===================================================== */

  useEffect(() => {
    setIsVisible(false);

    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 120);

    return () => clearTimeout(timeout);
  }, [currentIndex]);

  /* =====================================================
     SLIDER CONTROLS
  ===================================================== */

  const goToNext = () => {
    if (!slides.length) return;

    setCurrentIndex((previous) => (previous + 1) % slides.length);
  };

  const goToPrevious = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (previous) => (previous - 1 + slides.length) % slides.length
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <section className="slider-loader">
        <div className="loader-content">
          <div className="loader-logo">
            <FaGraduationCap />
          </div>

          <div className="spinner"></div>

          <h3>Preparing Your Learning Experience</h3>

          <p>Loading school information...</p>
        </div>
      </section>
    );
  }

  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <section className="slider-loader error">
        <div className="loader-content">
          <div className="error-icon">
            !
          </div>

          <h3>Something went wrong</h3>

          <p>{error}</p>

          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </section>
    );
  }

  /* =====================================================
     EMPTY SLIDES
  ===================================================== */

  if (slides.length === 0) {
    return (
      <section className="slider-loader">
        <div className="loader-content">
          <div className="loader-logo">
            <FaGraduationCap />
          </div>

          <h3>Welcome to Our School</h3>

          <p>No slides are currently available.</p>
        </div>
      </section>
    );
  }

  const currentSlide = slides[currentIndex];

  return (
    <header className="school-header">
      {/* =================================================
          BACKGROUND SLIDES
      ================================================= */}

      <div className="school-slider-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id || index}
            className={`school-slide ${
              currentIndex === index ? "active" : ""
            }`}
            style={{
              backgroundImage: `
                linear-gradient(
                  120deg,
                  rgba(7, 22, 48, 0.88),
                  rgba(13, 47, 79, 0.68),
                  rgba(224, 163, 22, 0.28)
                ),
                url("${slide.image}")
              `,
            }}
          >
            {currentIndex === index && (
              <div
                className={`school-text ${
                  isVisible ? "animate" : ""
                }`}
              >
                <div className="hero-badge">
                  <FaGraduationCap />
                  <span>EXCELLENCE IN EDUCATION</span>
                </div>

                <h1>{slide.title}</h1>

                <div className="hero-divider"></div>

                <p>{slide.description}</p>

                <div className="hero-actions">
                  <a href="#about" className="hero-main-button">
                    Discover More
                    <FaArrowRight />
                  </a>

                  <button className="hero-play-button">
                    <span>
                      <FaPlay />
                    </span>
                    Explore Our School
                  </button>
                </div>

                <div className="hero-check-list">
                  <div>
                    <FaCheckCircle />
                    <span>Quality Education</span>
                  </div>

                  <div>
                    <FaCheckCircle />
                    <span>Student Excellence</span>
                  </div>

                  <div>
                    <FaCheckCircle />
                    <span>Strong Character</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* =================================================
            SLIDE ARROWS
        ================================================= */}

        {slides.length > 1 && (
          <div className="slider-navigation">
            <button
              className="slider-arrow"
              onClick={goToPrevious}
              aria-label="Previous slide"
            >
              <FaChevronLeft />
            </button>

            <button
              className="slider-arrow"
              onClick={goToNext}
              aria-label="Next slide"
            >
              <FaChevronRight />
            </button>
          </div>
        )}

        {/* =================================================
            SLIDE NUMBER
        ================================================= */}

        <div className="slide-counter">
          <span>
            {String(currentIndex + 1).padStart(2, "0")}
          </span>

          <div className="counter-line"></div>

          <span>
            {String(slides.length).padStart(2, "0")}
          </span>
        </div>
      </div>

      {/* =================================================
          RIGHT INFORMATION PANEL
      ================================================= */}

      <aside className="school-right-panel">
        {/* ANNOUNCEMENT */}

        <div className="announcement-box">
          <div className="announcement-top">
            <div className="announcement-icon">
              <FaBullhorn />
            </div>

            <div>
              <span>IMPORTANT</span>
              <h3>Latest Announcement</h3>
            </div>
          </div>

          <div className="announcement-content">
            <h4>Admissions Are Open</h4>

            <p>
              Admission is currently open. Parents and guardians
              are invited to register their children for the
              upcoming academic year.
            </p>

            <button className="announcement-button">
              Learn More
              <FaArrowRight />
            </button>
          </div>
        </div>

        {/* FEATURE CARDS */}

        <div className="school-info-cards">
          <div className="school-card">
            <div className="school-card-icon">
              <FaGraduationCap />
            </div>

            <div>
              <h3>Academic Excellence</h3>

              <p>
                Building strong foundations through quality
                teaching, discipline, and innovation.
              </p>
            </div>
          </div>

          <div className="school-card">
            <div className="school-card-icon teacher">
              <FaChalkboardTeacher />
            </div>

            <div>
              <h3>Qualified Teachers</h3>

              <p>
                Experienced educators committed to helping every
                learner reach their full potential.
              </p>
            </div>
          </div>
        </div>

        {/* QUICK MESSAGE */}

        <div className="school-panel-message">
          <FaStar className="message-star" />

          <div>
            <strong>Inspiring Tomorrow's Leaders</strong>
            <span>
              Knowledge • Character • Excellence
            </span>
          </div>
        </div>
      </aside>

      {/* =================================================
          BOTTOM SLIDER CONTROLS
      ================================================= */}

      {slides.length > 1 && (
        <div className="school-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`school-indicator ${
                currentIndex === index ? "active" : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            >
              <span></span>
            </button>
          ))}
        </div>
      )}

      {/* =================================================
          CURRENT SLIDE LABEL
      ================================================= */}

      <div className="hero-bottom-label">
        <span>EXCELLENT KIDS FOUNDATION</span>
      </div>
    </header>
  );
};

export default HomeHeader;







// import React, { useState, useEffect } from "react";
// import api from "../api/api";
// import "./HomeHeaderSave.css";

// const HomeHeader = () => {
//   const [slides, setSlides] = useState([]);
//   const [currentIndex, setCurrentIndex] = useState(0);
//   const [isVisible, setIsVisible] = useState(true);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);


//   // Fetch slides
//   useEffect(() => {
//     const fetchSlides = async () => {
//       try {
//         const response = await api.get("/api/slides/");

//         console.log("Slides API Response:", response.data);

//         const data = response.data.results || response.data;

//         setSlides(Array.isArray(data) ? data : []);

//       } catch (err) {

//         console.error(
//           "Failed to load slides:",
//           err
//         );

//         setError(
//           "Unable to load slides"
//         );

//       } finally {

//         setLoading(false);

//       }
//     };


//     fetchSlides();

//   }, []);



//   // Auto slider
//   useEffect(() => {

//     if (slides.length <= 1) return;


//     const timer = setInterval(() => {

//       setCurrentIndex(
//         previous =>
//           (previous + 1) % slides.length
//       );

//     }, 5000);


//     return () =>
//       clearInterval(timer);


//   }, [slides]);




//   // Restart text animation when slide changes
//   useEffect(() => {

//     setIsVisible(false);


//     const timeout = setTimeout(() => {

//       setIsVisible(true);

//     }, 100);


//     return () =>
//       clearTimeout(timeout);


//   }, [currentIndex]);





//   // Loading state
//   if (loading) {

//     return (

//       <div className="slider-loader">

//         <div className="spinner"></div>

//         <p>
//           Loading slides...
//         </p>

//       </div>

//     );

//   }



//   // Error state
//   if (error) {

//     return (

//       <div className="slider-loader error">

//         <p>
//           {error}
//         </p>

//       </div>

//     );

//   }





//   // Empty slides
//   if (slides.length === 0) {

//     return (

//       <div className="slider-loader">

//         <p>
//           No slides available
//         </p>

//       </div>

//     );

//   }




// return (

// <header className="school-header">


// <div className="school-slider-container">


// {
// slides.map((slide,index)=>(


// <div

// key={slide.id}

// className={
// `
// school-slide
// ${currentIndex===index?"active":""}
// `
// }


// style={{

// backgroundImage:

// `
// linear-gradient(
// rgba(0,0,0,.45),
// rgba(0,0,0,.45)
// ),
// url("${slide.image}")
// `

// }}


// >



// {
// currentIndex===index && (


// <div

// className={
// `
// school-text
// ${isVisible?"animate":""}
// `
// }


// >


// <h1>

// {slide.title}

// </h1>



// <p>

// {slide.description}

// </p>


// </div>


// )

// }


// </div>


// ))

// }




// {/* RIGHT SIDE SCHOOL INFORMATION */}


// <div className="school-right-panel">


// <div className="announcement-box">


// <div className="announcement-header">

// 📢 Latest Announcement

// </div>


// <p>

// Admission is currently open.
// Parents are invited to register their children
// for the upcoming academic year.

// </p>


// <button>

// Learn More

// </button>


// </div>




// <div className="school-info-cards">


// <div className="school-card">

// <h3>
// Academic Excellence
// </h3>

// <p>
// Building strong foundations through quality teaching and discipline.
// </p>

// </div>



// <div className="school-card">

// <h3>
// Qualified Teachers
// </h3>

// <p>
// Experienced educators dedicated to student success.
// </p>

// </div>



// {/* <div className="school-card">

// <h3>
// Modern Learning
// </h3>

// <p>
// Preparing students for future challenges.
// </p>

// </div> */}


// </div>



// </div>






// <div className="school-indicators">


// {
// slides.map((_,index)=>(


// <button

// key={index}

// className={
// `
// school-indicator
// ${currentIndex===index?"active":""}
// `
// }


// onClick={()=>setCurrentIndex(index)}


// />


// ))

// }


// </div>



// </div>



// </header>


// );

// };


// export default HomeHeader;


